import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Users, GraduationCap, CheckCircle2, AlertTriangle, ClipboardList } from 'lucide-react';
import { KPICard, LoadingSpinner, ErrorPanel, ResponsiveStyles } from '../../components/dashboard/DashboardShared';
import { useQuery } from '@tanstack/react-query';
import '../../components/ui/datatable.css';

const API = import.meta.env.VITE_API_URL || '/api/v1';

export function DashboardTurma() {
  const { escolaId, turmaId } = useParams<{ escolaId: string; turmaId: string }>();
  const token = localStorage.getItem('ac_token');

  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard-turma', escolaId, turmaId],
    queryFn: async () => {
      const res = await fetch(`${API}/escolas/${escolaId}/turmas/${turmaId}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      return res.json();
    },
    enabled: !!escolaId && !!turmaId,
    refetchInterval: 15000,
  });

  const [filtro, setFiltro] = useState<'todos' | 'presentes' | 'ausentes'>('todos');

  if (isLoading) return <LoadingSpinner label="Carregando Dashboard da Turma..." />;
  if (error) return <ErrorPanel />;

  const k = data?.kpis || {};
  const turma = data?.turma || {};

  const alunosFiltrados = (data?.studentList || []).filter((a: any) => {
    if (filtro === 'presentes') return ['PRESENTE', 'ATRASADO'].includes(a.statusDiario);
    if (filtro === 'ausentes') return a.statusDiario === 'AUSENTE';
    return true;
  });

  const statusColors: Record<string, { bg: string; color: string }> = {
    PRESENTE: { bg: '#f0fdf4', color: '#16a34a' },
    ATRASADO: { bg: '#fffbeb', color: '#d97706' },
    AUSENTE: { bg: '#fef2f2', color: '#dc2626' },
    SAIU: { bg: '#f8fafc', color: '#64748b' },
  };

  return (
    <div className="admin-page animate-fadeIn">
      <div className="page-header">
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Users size={28} style={{ color: '#6366f1' }} />
            {turma.nome || 'Turma'} — {turma.serie || ''}
          </h1>
          <p>{turma.escola?.nome} · Turno: {turma.turno} · {new Date().toLocaleDateString('pt-BR')}</p>
        </div>
      </div>

      <div className="kpi-grid kpi-grid-4">
        <KPICard icon={GraduationCap} label="Matriculados" value={k.totalMatriculados || 0} color="#6366f1" />
        <KPICard icon={CheckCircle2} label="Presentes" value={k.totalPresentes || 0} color="#16a34a" />
        <KPICard icon={AlertTriangle} label="Ausentes" value={k.totalAusentes || 0} color="#dc2626" />
        <KPICard icon={ClipboardList} label="Chamadas Hoje" value={k.totalChamadasHoje || 0} sub={`${k.totalChamadasConcluidas || 0} concluídas`} color="#0891b2" />
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 8, marginTop: 20, marginBottom: 16 }}>
        {(['todos', 'presentes', 'ausentes'] as const).map(f => (
          <button key={f} onClick={() => setFiltro(f)} style={{
            padding: '6px 16px', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)',
            background: filtro === f ? '#6366f1' : 'var(--bg-card, white)',
            color: filtro === f ? 'white' : 'var(--text-main, #333)',
            fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', transition: 'all 0.2s',
          }}>
            {f === 'todos' ? `Todos (${data?.studentList?.length || 0})` : f === 'presentes' ? `Presentes (${k.totalPresentes || 0})` : `Ausentes (${k.totalAusentes || 0})`}
          </button>
        ))}
      </div>

      {/* Lista de Alunos */}
      <div style={{ background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)', padding: 20 }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Users size={18} style={{ color: '#6366f1' }} /> Alunos ({alunosFiltrados.length})
        </h3>
        {alunosFiltrados.length === 0 ? (
          <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>Nenhum aluno nesta categoria</p>
        ) : (
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            <table className="dt-table" style={{ width: '100%', fontSize: '0.82rem' }}>
              <thead><tr><th>#</th><th>Nome</th><th>Matrícula</th><th>Entrada</th><th>Status</th><th>Inconsist.</th></tr></thead>
              <tbody>
                {alunosFiltrados.map((a: any, i: number) => {
                  const sc = statusColors[a.statusDiario] || statusColors.AUSENTE;
                  return (
                    <tr key={a.id}>
                      <td>{i + 1}</td>
                      <td style={{ fontWeight: 600 }}>{a.nome}</td>
                      <td><code>{a.matricula}</code></td>
                      <td>{a.statusEntrada === 'ENTROU' ? '🟢 Entrou' : '⚪ Não'}</td>
                      <td><span style={{ padding: '2px 10px', borderRadius: 8, fontSize: '0.72rem', fontWeight: 600, background: sc.bg, color: sc.color }}>{a.statusDiario}</span></td>
                      <td>{a.inconsistencia ? <span style={{ color: '#d97706' }}>⚠️ {a.motivoInconsistencia || 'Sim'}</span> : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Alertas da turma */}
      {(data?.alertas || []).length > 0 && (
        <div style={{ background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)', padding: 20, marginTop: 24 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={18} style={{ color: '#d97706' }} /> Alertas da Turma
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(data.alertas || []).map((a: any) => (
              <div key={a.id} style={{ padding: '10px 14px', background: a.severidade === 'CRITICA' ? '#fef2f2' : '#fffbeb', borderRadius: 10, borderLeft: `3px solid ${a.severidade === 'CRITICA' ? '#dc2626' : '#d97706'}` }}>
                <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{a.titulo}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{a.descricao}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ResponsiveStyles />
    </div>
  );
}
